import React, { useState, useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import './MyPayments.css'
import '../styles/dashboard-header.css'
import LoadingScreen from '../components/LoadingScreen'
import { alert } from '../utils/alertService'

const MyPayments = () => {
  const [payments, setPayments] = useState([])
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const history = useHistory()
  const location = useLocation()

  useEffect(() => {
    const token = localStorage.getItem('access_token')
    if (!token) {
      history.push('/login')
      return
    }
    const userData = JSON.parse(localStorage.getItem('user') || '{}')
    if (userData.role !== 'student') {
      history.push('/login')
      return
    }

    const testId = new URLSearchParams(location.search).get('testId')
    if (testId) {
      // Arrived from a locked exam: issue (or fetch) its challan, then list.
      generateChallan(testId)
    } else {
      fetchPayments()
    }
  }, [])

  const fetchPayments = async () => {
    try {
      const token = localStorage.getItem('access_token')
      const response = await fetch('/api/payments/history?per_page=50', {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (response.ok) {
        const data = await response.json()
        setPayments(data.payments || [])
      } else {
        alert.error('Failed to load your payments')
      }
    } catch (err) {
      alert.error('Network error while loading payments')
    } finally {
      setLoading(false)
    }
  }

  const generateChallan = async (testId) => {
    setGenerating(true)
    try {
      const token = localStorage.getItem('access_token')
      const response = await fetch('/api/payments/initiate', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ payment_method: 'challan', test_id: Number(testId) })
      })
      const data = await response.json()
      if (response.ok) {
        alert.success('Your challan is ready. Pay at the bank using the PSID shown below.')
      } else {
        alert.error(data.error || 'Could not generate a challan for this exam')
      }
    } catch (err) {
      alert.error('Network error while generating the challan')
    } finally {
      setGenerating(false)
      fetchPayments()
    }
  }

  const downloadSlip = async (paymentId) => {
    try {
      const token = localStorage.getItem('access_token')
      const response = await fetch(`/api/payments/${paymentId}/slip`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (!response.ok) {
        alert.error('Could not download the challan slip')
        return
      }
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `challan_${paymentId}.pdf`
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      alert.error('Download failed')
    }
  }

  const statusClass = (status) => {
    if (status === 'completed') return 'paid'
    if (status === 'pending') return 'pending'
    return 'failed'
  }

  const statusLabel = (status) => {
    if (status === 'completed') return 'Paid'
    if (status === 'pending') return 'Unpaid'
    if (status === 'failed') return 'Failed'
    if (status === 'cancelled') return 'Cancelled'
    return status
  }

  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '-')

  const handleLogout = () => {
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('user')
    history.push('/login')
  }

  if (loading) {
    return <LoadingScreen message="Loading Your Payments" />
  }

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="header-content">
          <h1>My Payments</h1>
          <div className="user-info">
            <button onClick={() => history.push('/student-dashboard')} className="logout-button" style={{ background: 'rgba(255,255,255,0.2)' }}>
              Back to Dashboard
            </button>
            <button onClick={handleLogout} className="logout-button">Logout</button>
          </div>
        </div>
      </header>

      <main className="dashboard-main">
        {generating && <p className="mp-note">Preparing your challan...</p>}

        {payments.length === 0 ? (
          <div className="mp-empty">
            <div className="mp-empty-icon">🧾</div>
            <h3>No fees or challans yet</h3>
            <p>When an exam requires a fee, generate its challan from the Available Tests page and it will appear here.</p>
            <button className="mp-btn" onClick={() => history.push('/available-tests')}>View Available Tests</button>
          </div>
        ) : (
          <div className="mp-list">
            {payments.map((p) => (
              <div key={p.id} className="mp-card">
                <div className="mp-card-head">
                  <div>
                    <h3>{p.test_title || 'Exam fee'}</h3>
                    <span className="mp-sub">{p.payment_method === 'challan' ? 'Bank Challan' : p.payment_method}</span>
                  </div>
                  <span className={`mp-pill ${statusClass(p.status)}`}>{statusLabel(p.status)}</span>
                </div>

                <div className="mp-grid">
                  {p.psid && (
                    <div className="mp-field mp-field-psid">
                      <span className="mp-label">PSID (pay against this)</span>
                      <span className="mp-psid">{p.psid}</span>
                    </div>
                  )}
                  <div className="mp-field">
                    <span className="mp-label">Amount</span>
                    <span className="mp-value">{p.currency || 'PKR'} {Number(p.amount).toLocaleString()}</span>
                  </div>
                  {p.challan_number && (
                    <div className="mp-field">
                      <span className="mp-label">Challan No.</span>
                      <span className="mp-value">{p.challan_number}</span>
                    </div>
                  )}
                  <div className="mp-field">
                    <span className="mp-label">Due date</span>
                    <span className="mp-value">{fmtDate(p.due_date)}</span>
                  </div>
                  {p.paid_at && (
                    <div className="mp-field">
                      <span className="mp-label">Paid on</span>
                      <span className="mp-value">{fmtDate(p.paid_at)}</span>
                    </div>
                  )}
                </div>

                {p.payment_method === 'challan' && p.psid && (
                  <div className="mp-actions">
                    <button className="mp-btn" onClick={() => downloadSlip(p.id)}>Download Slip</button>
                    {p.status === 'pending' && (
                      <span className="mp-hint">Pay at any Allied Bank branch or 1LINK using the PSID. Status updates to Paid once confirmed.</span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  )
}

export default MyPayments
